import { supabase } from '../integrations/supabase/client';
import { Database } from '../integrations/supabase/types';

type FinancialEntry = Database['public']['Tables']['financial_entries']['Row'];
type FinancialEntryInsert = Database['public']['Tables']['financial_entries']['Insert'];
type FinancialEntryUpdate = Database['public']['Tables']['financial_entries']['Update'];

export interface CreateFinancialEntryData {
  description: string;
  amount: number;
  entry_type: 'income' | 'expense';
  category: string;
  case_id?: string;
  client_id?: string;
  payment_date?: string;
  due_date?: string;
  status?: string;
  notes?: string;
  is_installment?: boolean;
  installments?: number;
  installment_frequency?: string;
  down_payment?: number;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  payment_method?: string;
}

export class FinancialEntriesService {
  /**
   * Cria uma nova entrada financeira
   */
  static async createEntry(data: CreateFinancialEntryData, userId: string): Promise<FinancialEntry> {
    const entryData: FinancialEntryInsert = {
      ...data,
      user_id: userId,
      status: data.status || 'pending',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data: entry, error } = await supabase
      .from('financial_entries')
      .insert(entryData)
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar entrada financeira:', error);
      throw new Error(`Erro ao criar entrada financeira: ${error.message}`);
    }

    return entry;
  }

  /**
   * Busca entradas financeiras por caso
   */
  static async getEntriesByCase(caseId: string, userId: string): Promise<FinancialEntry[]> {
    const { data: entries, error } = await supabase
      .from('financial_entries')
      .select('*')
      .eq('case_id', caseId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar entradas financeiras:', error);
      throw new Error(`Erro ao buscar entradas financeiras: ${error.message}`);
    }

    return entries || [];
  }

  /**
   * Busca entradas financeiras por cliente
   */
  static async getEntriesByClient(clientId: string, userId: string): Promise<FinancialEntry[]> {
    const { data: entries, error } = await supabase
      .from('financial_entries')
      .select('*')
      .eq('client_id', clientId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar entradas financeiras:', error);
      throw new Error(`Erro ao buscar entradas financeiras: ${error.message}`);
    }

    return entries || [];
  }

  /**
   * Atualiza uma entrada financeira
   */
  static async updateEntry(entryId: string, data: Partial<CreateFinancialEntryData>, userId: string): Promise<FinancialEntry> {
    const updateData: FinancialEntryUpdate = {
      ...data,
      updated_at: new Date().toISOString()
    };

    const { data: entry, error } = await supabase
      .from('financial_entries')
      .update(updateData)
      .eq('id', entryId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar entrada financeira:', error);
      throw new Error(`Erro ao atualizar entrada financeira: ${error.message}`);
    }

    return entry;
  }

  /**
   * Remove uma entrada financeira
   */
  static async deleteEntry(entryId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('financial_entries')
      .delete()
      .eq('id', entryId)
      .eq('user_id', userId);

    if (error) {
      console.error('Erro ao remover entrada financeira:', error);
      throw new Error(`Erro ao remover entrada financeira: ${error.message}`);
    }
  }

  /**
   * Cria entradas financeiras para um caso baseado nos dados do formulário
   */
  static async createCaseFinancialEntries(
    caseId: string,
    clientId: string,
    financialData: {
      client_fee?: number;
      court_costs?: number;
      success_fee?: number;
      collection_notes?: string;
      phase_fees?: any[];
      success_fee_type?: string;
      success_fee_conditions?: string;
    },
    userId: string
  ): Promise<FinancialEntry[]> {
    const entries: CreateFinancialEntryData[] = [];

    // Honorários do cliente
    if (financialData.client_fee && financialData.client_fee > 0) {
      entries.push({
        description: 'Honorários do Cliente',
        amount: financialData.client_fee,
        entry_type: 'income',
        category: 'honorarios',
        case_id: caseId,
        client_id: clientId,
        status: 'pending',
        notes: 'Honorários contratuais do cliente'
      });
    }

    // Custas judiciais
    if (financialData.court_costs && financialData.court_costs > 0) {
      entries.push({
        description: 'Custas Judiciais',
        amount: financialData.court_costs,
        entry_type: 'expense',
        category: 'custas',
        case_id: caseId,
        client_id: clientId,
        status: 'pending',
        notes: 'Custas e despesas processuais'
      });
    }

    // Taxa de êxito
    if (financialData.success_fee && financialData.success_fee > 0) {
      entries.push({
        description: 'Taxa de Êxito',
        amount: financialData.success_fee,
        entry_type: 'income',
        category: 'taxa_exito',
        case_id: caseId,
        client_id: clientId,
        status: 'conditional',
        is_conditional_payment: true,
        conditional_type: financialData.success_fee_type || 'percentage',
        notes: financialData.success_fee_conditions || 'Taxa de êxito condicionada ao resultado positivo'
      });
    }

    // Honorários de cobrança
    if (financialData.collection_notes) {
      entries.push({
        description: 'Honorários de Cobrança',
        amount: 0, // Valor a ser definido posteriormente
        entry_type: 'income',
        category: 'cobranca',
        case_id: caseId,
        client_id: clientId,
        status: 'pending',
        notes: financialData.collection_notes
      });
    }

    // Honorários por fase
    if (financialData.phase_fees && Array.isArray(financialData.phase_fees)) {
      financialData.phase_fees.forEach((phase, index) => {
        if (phase.amount && phase.amount > 0) {
          entries.push({
            description: `Honorários - ${phase.phase || `Fase ${index + 1}`}`,
            amount: phase.amount,
            entry_type: 'income',
            category: 'honorarios_fase',
            case_id: caseId,
            client_id: clientId,
            status: 'pending',
            notes: phase.description || `Honorários da ${phase.phase || `fase ${index + 1}`}`
          });
        }
      });
    }

    // Criar todas as entradas
    const createdEntries: FinancialEntry[] = [];
    for (const entryData of entries) {
      try {
        const entry = await this.createEntry(entryData, userId);
        createdEntries.push(entry);
      } catch (error) {
        console.error('Erro ao criar entrada financeira:', error);
        // Continua criando as outras entradas mesmo se uma falhar
      }
    }

    return createdEntries;
  }

  /**
   * Calcula o total de entradas por tipo para um caso
   */
  static async getCaseFinancialSummary(caseId: string, userId: string): Promise<{
    totalIncome: number;
    totalExpenses: number;
    pendingIncome: number;
    pendingExpenses: number;
    paidIncome: number;
    paidExpenses: number;
  }> {
    const entries = await this.getEntriesByCase(caseId, userId);

    const summary = {
      totalIncome: 0,
      totalExpenses: 0,
      pendingIncome: 0,
      pendingExpenses: 0,
      paidIncome: 0,
      paidExpenses: 0
    };

    entries.forEach(entry => {
      const amount = entry.amount || 0;
      
      if (entry.entry_type === 'income') {
        summary.totalIncome += amount;
        if (entry.status === 'paid') {
          summary.paidIncome += amount;
        } else {
          summary.pendingIncome += amount;
        }
      } else if (entry.entry_type === 'expense') {
        summary.totalExpenses += amount;
        if (entry.status === 'paid') {
          summary.paidExpenses += amount;
        } else {
          summary.pendingExpenses += amount;
        }
      }
    });

    return summary;
  }
}

export default FinancialEntriesService;